import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

export type LiveTranscriptRole = 'user' | 'model';

export interface LiveTranscriptChunk {
  role: LiveTranscriptRole;
  text: string;
  isFinal?: boolean;
  isDelta?: boolean;
}

export interface GoogleAudioSessionOptions {
  model?: string;
  voice?: string;
  systemInstruction?: string;
  responseModalities?: Array<'AUDIO' | 'TEXT'>;
}

@Injectable({ providedIn: 'root' })
export class GoogleAudioService implements OnDestroy {
  private readonly apiKey = environment.GEMINI_API_KEY;
  private readonly defaultModel = 'gemini-2.5-flash-native-audio-preview-12-2025';
  private readonly defaultInstruction = 'You are a helpful and friendly AI assistant.';
  private readonly wsBaseUrl =
    'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService/streamGenerateContent';

  readonly sessionActive$ = new BehaviorSubject<boolean>(false);
  readonly transcript$ = new Subject<LiveTranscriptChunk>();
  readonly error$ = new Subject<unknown>();

  private ws: WebSocket | null = null;
  private micStream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private outputCtx: AudioContext | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private remoteAnalyser: AnalyserNode | null = null;
  private micProcessor: ScriptProcessorNode | null = null;
  private muted = false;
  private nextPlaybackTime = 0;
  private currentUserTranscript = '';
  private currentModelTranscript = '';

  constructor(private zone: NgZone) {}

  async startLiveSession(opts?: GoogleAudioSessionOptions): Promise<{
    close: () => void;
    transcript$: Subject<LiveTranscriptChunk>;
  }> {
    if (!this.apiKey) throw new Error('Missing GEMINI_API_KEY for Gemini Live audio.');
    if (this.sessionActive$.value) {
      return { close: () => this.stopSession(), transcript$: this.transcript$ };
    }

    await this.connectWebSocket(opts);
    await this.startMicCapture();

    this.sessionActive$.next(true);

    return { close: () => this.stopSession(), transcript$: this.transcript$ };
  }

  muteMic(muted: boolean): void {
    this.muted = muted;
    if (this.micStream) {
      this.micStream.getAudioTracks().forEach((track) => (track.enabled = !muted));
    }
  }

  getAnalyzers(): { audioCtx: AudioContext | null; mic: AnalyserNode | null; remote: AnalyserNode | null } {
    return {
      audioCtx: this.audioCtx,
      mic: this.micAnalyser,
      remote: this.remoteAnalyser
    };
  }

  ngOnDestroy(): void {
    this.stopSession();
  }

  private async connectWebSocket(opts?: GoogleAudioSessionOptions): Promise<void> {
    const url = new URL(this.wsBaseUrl);
    url.searchParams.set('key', this.apiKey);

    this.ws = new WebSocket(url.toString());

    await new Promise<void>((resolve, reject) => {
      if (!this.ws) return reject(new Error('Failed to create WebSocket.'));

      const handleOpen = () => {
        this.ws?.removeEventListener('open', handleOpen);
        this.ws?.removeEventListener('error', handleError);
        resolve();
      };
      const handleError = (event: Event) => {
        this.ws?.removeEventListener('open', handleOpen);
        this.ws?.removeEventListener('error', handleError);
        reject(event);
      };

      this.ws.addEventListener('open', handleOpen);
      this.ws.addEventListener('error', handleError);
    });

    this.ws.onmessage = (event) => this.handleServerMessage(event.data);
    this.ws.onerror = (event) => this.error$.next(event);
    this.ws.onclose = () => this.sessionActive$.next(false);

    const model = opts?.model || this.defaultModel;
    const instruction = opts?.systemInstruction || this.defaultInstruction;
    const responseModalities = opts?.responseModalities || ['AUDIO', 'TEXT'];

    const config: any = {
      response_modalities: responseModalities,
      system_instruction: instruction
    };

    if (opts?.voice) {
      config.speech_config = { voice_config: { voice_name: opts.voice } };
    }

    this.sendMessage({
      setup: {
        model: `models/${model}`,
        config
      }
    });
  }

  private async startMicCapture(): Promise<void> {
    this.audioCtx = new AudioContext();
    this.outputCtx = new AudioContext();
    this.nextPlaybackTime = 0;

    this.micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1
      }
    });

    const source = this.audioCtx.createMediaStreamSource(this.micStream);
    this.micAnalyser = this.audioCtx.createAnalyser();
    source.connect(this.micAnalyser);

    this.micProcessor = this.audioCtx.createScriptProcessor(2048, 1, 1);
    const silentGain = this.audioCtx.createGain();
    silentGain.gain.value = 0;

    source.connect(this.micProcessor);
    this.micProcessor.connect(silentGain);
    silentGain.connect(this.audioCtx.destination);

    this.micProcessor.onaudioprocess = (event) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.muted) return;
      const input = event.inputBuffer.getChannelData(0);
      const pcm = this.downsampleToPcm16(input, this.audioCtx?.sampleRate ?? 48000, 16000);
      const base64 = this.toBase64(pcm);

      this.sendMessage({
        realtimeInput: {
          mediaChunks: [
            {
              mimeType: 'audio/pcm;rate=16000',
              data: base64
            }
          ]
        }
      });
    };
  }

  private handleServerMessage(raw: string): void {
    let message: any;
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }

    if (message?.error) {
      this.error$.next(message.error);
      return;
    }

    const serverContent = message?.serverContent ?? message?.server_content;

    const inputTranscript =
      serverContent?.inputTranscription ||
      serverContent?.input_transcription ||
      message?.inputTranscription ||
      message?.input_transcription;

    if (inputTranscript?.text || inputTranscript?.transcript) {
      const text = String(inputTranscript.text ?? inputTranscript.transcript ?? '');
      const isFinal = Boolean(inputTranscript.isFinal ?? inputTranscript.final ?? false);
      this.updateTranscript('user', text, isFinal, true);
    }

    const modelTurn = serverContent?.modelTurn || serverContent?.model_turn;
    const turnComplete = Boolean(serverContent?.turnComplete ?? serverContent?.turn_complete ?? false);

    if (Array.isArray(modelTurn?.parts)) {
      modelTurn.parts.forEach((part: any) => {
        if (typeof part?.text === 'string' && part.text.trim()) {
          this.updateTranscript('model', part.text, false, true);
        }

        const inline = part?.inlineData || part?.inline_data;
        const audioData = inline?.data;
        if (audioData) this.playAudioChunk(audioData, 24000);
      });
    }

    if (turnComplete && this.currentModelTranscript) {
      this.updateTranscript('model', this.currentModelTranscript, true, false);
      this.currentModelTranscript = '';
    }
  }

  private updateTranscript(role: LiveTranscriptRole, text: string, isFinal: boolean, isDelta: boolean): void {
    if (!text) return;

    if (role === 'user') {
      if (isDelta) {
        this.currentUserTranscript = text;
      } else {
        this.currentUserTranscript = text;
      }
      this.zone.run(() =>
        this.transcript$.next({
          role,
          text: this.currentUserTranscript,
          isFinal,
          isDelta
        })
      );

      if (isFinal) this.currentUserTranscript = '';
      return;
    }

    if (role === 'model') {
      if (isDelta) {
        this.currentModelTranscript += text;
      } else {
        this.currentModelTranscript = text;
      }

      this.zone.run(() =>
        this.transcript$.next({
          role,
          text: this.currentModelTranscript,
          isFinal,
          isDelta
        })
      );

      if (isFinal) this.currentModelTranscript = '';
    }
  }

  private playAudioChunk(base64: string, sampleRate: number): void {
    if (!this.outputCtx) return;
    const bytes = this.fromBase64(base64);
    if (!bytes) return;

    const pcm = new Int16Array(bytes.buffer);
    const audioBuffer = this.outputCtx.createBuffer(1, pcm.length, sampleRate);
    const channel = audioBuffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) {
      channel[i] = pcm[i] / 32768;
    }

    const source = this.outputCtx.createBufferSource();
    source.buffer = audioBuffer;

    if (!this.remoteAnalyser) {
      this.remoteAnalyser = this.outputCtx.createAnalyser();
    }

    source.connect(this.remoteAnalyser);
    this.remoteAnalyser.connect(this.outputCtx.destination);

    const startTime = Math.max(this.outputCtx.currentTime, this.nextPlaybackTime);
    source.start(startTime);
    this.nextPlaybackTime = startTime + audioBuffer.duration;
  }

  private downsampleToPcm16(buffer: Float32Array, inputRate: number, outputRate: number): Int16Array {
    if (outputRate === inputRate) {
      return this.floatTo16BitPCM(buffer);
    }

    const sampleRateRatio = inputRate / outputRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Int16Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0;
      let count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }
      const sample = accum / (count || 1);
      result[offsetResult] = Math.max(-1, Math.min(1, sample)) * 0x7fff;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  private floatTo16BitPCM(buffer: Float32Array): Int16Array {
    const output = new Int16Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) {
      const s = Math.max(-1, Math.min(1, buffer[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output;
  }

  private toBase64(data: Int16Array): string {
    const bytes = new Uint8Array(data.buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  }

  private fromBase64(base64: string): Uint8Array | null {
    try {
      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
      return bytes;
    } catch {
      return null;
    }
  }

  private sendMessage(payload: unknown): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify(payload));
    } catch (error) {
      this.error$.next(error);
    }
  }

  private stopSession(): void {
    try {
      this.ws?.close();
    } catch {}
    this.ws = null;

    if (this.micProcessor) {
      try {
        this.micProcessor.disconnect();
      } catch {}
      this.micProcessor.onaudioprocess = null;
    }

    if (this.micStream) {
      this.micStream.getTracks().forEach((track) => track.stop());
      this.micStream = null;
    }

    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
    }

    if (this.outputCtx) {
      this.outputCtx.close();
      this.outputCtx = null;
    }

    this.micAnalyser = null;
    this.remoteAnalyser = null;
    this.micProcessor = null;
    this.currentUserTranscript = '';
    this.currentModelTranscript = '';
    this.nextPlaybackTime = 0;

    this.sessionActive$.next(false);
  }
}
